import { type AskPunttProjectReviewState } from "@mg/schemas/src/christo/askPunttProject";
import {
  ArrowLineLeft,
  ArrowLineRight,
  CaretRight,
  ChatCircle,
  ChatText,
  CircleNotch,
  Gear,
  House,
  Kanban,
  PencilSimple,
  Plus,
  Trash,
  UserCircle,
  UserPlus,
} from "@phosphor-icons/react";
import {
  Box,
  Button,
  Dialog,
  DropdownMenu,
  Flex,
  IconButton,
  Link,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import {
  Link as NavLink,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import cx from "classnames";
import { formatDistanceToNow } from "date-fns";
import { usePostHog } from "posthog-js/react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { FolderTreeView } from "./AppDrawer";
import { AvatarWithInitials } from "./AvatarWithInitials";
import { EditProfileDialog } from "./EditProfileDialog";
import EnterpriseSwitcher from "./EnterpriseSwitcher";
import { useAutoCollapseSidebar } from "./hooks/useAutoCollapseSidebar";
import { InvitePeopleDialog } from "./InvitePeopleDialog";
import { Sidebar } from "./sidebar/Sidebar";
import { SidebarContent } from "./sidebar/SidebarContent";
import { SidebarFooter } from "./sidebar/SidebarFooter";
import { SidebarGroup } from "./sidebar/SidebarGroup";
import { SidebarHeader } from "./sidebar/SidebarHeader";
import {
  SIDEBAR_ACTIVE_ROW_CLASS,
  SIDEBAR_ITEM_INDENT,
  SIDEBAR_ITEM_META_CLASS,
  SIDEBAR_ITEM_META_STYLE,
  SIDEBAR_SECTION_HEADER_INDENT,
  SidebarActiveBar,
  SidebarRowTitle,
} from "./sidebar/sidebarItemStyles";
import { SidebarMenu } from "./sidebar/SidebarMenu";
import { SidebarMenuButton } from "./sidebar/SidebarMenuButton";
import { SidebarMenuItem } from "./sidebar/SidebarMenuItem";
import { SidebarProvider } from "./sidebar/SidebarProvider";
import { SidebarRail } from "./sidebar/SidebarRail";
import { useSidebar } from "./sidebar/useSidebar";

import { ReactComponent as Logo } from "../images/Logo-blue.svg";
import { ReactComponent as Wordmark } from "../images/Logotype.svg";
import {
  campaignRoute,
  campaignSearch,
} from "../routes/ask-puntt/campaignRoute";
import { askPunttChatsRoute } from "../routes/ask-puntt/chatsRoute";
import { UnreadDot } from "../routes/ask-puntt/components/UnreadDot";
import { askPunttProjectsRoute } from "../routes/ask-puntt/projectsRoute";
import { askPunttRoute } from "../routes/ask-puntt/route";
import { useStartNewProjectChat } from "../routes/ask-puntt/useStartNewProjectChat";
import {
  askPunttChatNavLink,
  isAskPunttChatNavActive,
  isViewingAskPunttChat,
} from "../routes/ask-puntt/utils/chatNavigation";
import { indexRoute } from "../routes/index/route";
import { roiRoute } from "../routes/roi/route";
import { archivedUsersRoute } from "../routes/settings/routes/archivedUsersRoute";
import { enterpriseSettingsRoute } from "../routes/settings/routes/enterpriseSettingsRoute";
import { integrationSettingsRoute } from "../routes/settings/routes/integrationSettingsRoute";
import { knowledgeDatabaseRoute } from "../routes/settings/routes/knowledge-database/route";
import { knowledgeSettingRoute } from "../routes/settings/routes/knowledgeSettingRoute";
import { notificationsSettingsRoute } from "../routes/settings/routes/notificationsSettingsRoute";
import { usersRoute } from "../routes/settings/routes/usersRoute";
import { ticketsRoute } from "../routes/tickets/route";
import { useAnalytics } from "../utils/analytics";
import {
  canAccessAskPuntt,
  canManageBrandGuidelines,
  canAccessKnowledgeDatabase,
  canManageUsers,
  canInvitePeople,
  canAccessPunttDashboard,
  isMeaningfulGigsOrDemo,
  isVitaloraDemoEnterprise,
  isMeaningfulGigsRole,
  showDocumentsInSettings,
  showDocumentsInSidebar,
} from "../utils/auth";
import { isNil } from "../utils/fp";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import {
  useAskPunttChats,
  useDeleteAskPunttChat,
  ASK_PUNTT_UNREAD_LIST_POLL_MS,
} from "../utils/queries/askPuntt";
import {
  useAskPunttProjects,
  useDeleteAskPunttProject,
  useRenameAskPunttProject,
} from "../utils/queries/askPunttProjects";
import { resolveAvatarColors } from "../utils/resolveAvatarColors";
import { logout } from "../utils/slices/auth";
import { setDrawerOpen } from "../utils/slices/ui";

const navLinkClasses =
  "transition-colors [&_button]:transition-colors [&_button]:hover:bg-puntt-neutral-grayA-2";
const activeNavLinkClasses =
  "[&_button]:!bg-puntt-neutral-grayA-3 [&_button]:hover:!bg-puntt-neutral-grayA-4 [&_button]:active:!bg-puntt-neutral-grayA-5";

// Per-state label + dot color for the project review status line. Only ACTIVE
// review states earn a dot/label in the rail; settled outcomes
// (changes_requested, approved) render nothing so the Recents list is not a
// wall of repeated status noise (product ask: only surface active statuses like
// "In review").
const REVIEW_STATE_PRESENTATION: Record<
  AskPunttProjectReviewState,
  { label: string; dotClass: string } | null
> = {
  in_review: { label: "In review", dotClass: "bg-puntt-accent-9" },
  changes_requested: null,
  approved: null,
  none: null,
};

/** Recents row hover actions — title-line aligned, tight like ticket comments. */
const SIDEBAR_RECENTS_ACTIONS_WRAP =
  "absolute right-1.5 top-1.5 z-[2] flex items-center gap-0.5";
const SIDEBAR_RECENTS_ACTION_BTN =
  "hidden size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm group-hover/chat-item:flex";

/**
 * Meta line for a project row in the combined Recents stream. Always leads with
 * the "Project" kind label so a project is visually distinguishable from a chat
 * row (which leads with "Chat") now that both kinds share one list, then
 * appends the review-status dot + label when the project has an active review
 * state: "Project" / "Project · In review".
 */
function ProjectRecentMeta({ state }: { state?: AskPunttProjectReviewState }) {
  const presentation = state ? REVIEW_STATE_PRESENTATION[state] : null;
  return (
    <span
      className={SIDEBAR_ITEM_META_CLASS}
      style={SIDEBAR_ITEM_META_STYLE}
      data-testid="sidebar-ask-puntt-recent-meta"
    >
      <span className="shrink-0">Project</span>
      {presentation ? (
        <>
          <span aria-hidden>·</span>
          {/* Status span text stays a bare label ("In review") — the "·"
              separator lives OUTSIDE it so the e2e innerText contract holds. */}
          <span
            className="flex items-center gap-1.5 truncate"
            data-testid="sidebar-ask-puntt-project-status"
          >
            <span
              aria-hidden
              className={cx(
                "size-1.5 shrink-0 rounded-full",
                presentation.dotClass,
              )}
            />
            <span className="truncate">{presentation.label}</span>
          </span>
        </>
      ) : null}
    </span>
  );
}

/** Mirrors the backend `name` cap; the schema only enforces a non-empty name. */
const MAX_ASK_PUNTT_PROJECT_NAME_LENGTH = 200;

interface RecentProject {
  _id: string;
  name: string;
  reviewState?: AskPunttProjectReviewState;
  role?: "owner" | "member";
  ownerId?: string;
  hasUnread?: boolean;
}

/**
 * A single project row in the combined Recents stream. Renders the project
 * title as a nav link with its review-status meta, plus (owner-only) a
 * hover-revealed rename pencil and delete button. Rename lives here — in the
 * left nav — rather than in the workspace header (product ask: editing happens
 * in the left nav, the header title is nav). Editing swaps the title for an
 * inline input; Enter / blur saves, Escape cancels, mirroring the breadcrumb
 * rename. Each row is its own component so `useRenameAskPunttProject` is a
 * valid per-project hook call.
 */
function RecentProjectRow({
  project,
  active,
  canModify,
  onRequestDelete,
}: {
  project: RecentProject;
  active: boolean;
  canModify: boolean;
  onRequestDelete: (projectId: string) => void;
}) {
  const renameMutation = useRenameAskPunttProject(project._id);
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(project.name);
  // Guards so a single edit session commits at most once and an Escape-driven
  // blur does not also save.
  const doneRef = useRef(false);
  const cancelledRef = useRef(false);

  // Keep the draft in sync when the name changes from elsewhere while not
  // actively editing.
  useEffect(() => {
    if (!isRenaming) setDraft(project.name);
  }, [project.name, isRenaming]);

  function startRename() {
    doneRef.current = false;
    cancelledRef.current = false;
    setDraft(project.name);
    setIsRenaming(true);
  }

  function commit() {
    if (doneRef.current || cancelledRef.current) return;
    doneRef.current = true;
    const next = draft.trim();
    setIsRenaming(false);
    if (!next || next === project.name.trim()) {
      setDraft(project.name);
      return;
    }
    renameMutation.mutate(next);
  }

  function cancel() {
    cancelledRef.current = true;
    setDraft(project.name);
    setIsRenaming(false);
  }

  return (
    <div
      className={cx(
        `group/chat-item relative flex w-full items-center gap-4 rounded-md py-1.5 ${SIDEBAR_ITEM_INDENT} pr-2 transition-colors hover:bg-puntt-neutral-gray-3`,
        active && SIDEBAR_ACTIVE_ROW_CLASS,
      )}
      data-testid="sidebar-ask-puntt-recent-item"
      data-kind="project"
      data-project-id={project._id}
      data-active={active ? "true" : "false"}
    >
      {active ? <SidebarActiveBar /> : null}
      {isRenaming ? (
        <input
          autoFocus
          value={draft}
          onFocus={(e) => e.target.select()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          maxLength={MAX_ASK_PUNTT_PROJECT_NAME_LENGTH}
          aria-label="Project name"
          data-testid="sidebar-ask-puntt-rename-input"
          className="w-full min-w-0 rounded border border-puntt-accent-9 bg-base-white px-1.5 py-0.5 text-sm text-puntt-neutral-gray-12 outline-none focus:ring-1 focus:ring-puntt-accent-8"
        />
      ) : (
        <Link asChild size="2" className="flex-1 overflow-hidden">
          <NavLink
            to={campaignRoute.to}
            params={{ projectId: project._id }}
            search={campaignSearch()}
            className="flex w-full items-start gap-2 overflow-hidden"
          >
            <span className="flex flex-1 flex-col overflow-hidden">
              <span className="flex items-center gap-2">
                <SidebarRowTitle
                  name={project.name}
                  testId="sidebar-ask-puntt-recent-title"
                />
                {project.hasUnread ? (
                  <UnreadDot
                    testId={`sidebar-ask-puntt-recent-unread-${project._id}`}
                    ariaLabel="Unread"
                  />
                ) : null}
              </span>
              <ProjectRecentMeta state={project.reviewState} />
            </span>
          </NavLink>
        </Link>
      )}
      {canModify && !isRenaming ? (
        <div className={SIDEBAR_RECENTS_ACTIONS_WRAP}>
          <button
            type="button"
            className={cx(
              SIDEBAR_RECENTS_ACTION_BTN,
              "text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-4",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startRename();
            }}
            aria-label="Rename project"
            data-testid="sidebar-ask-puntt-rename-recent"
            disabled={renameMutation.isPending}
          >
            {renameMutation.isPending ? (
              <CircleNotch size={10} className="animate-spin" aria-hidden />
            ) : (
              <PencilSimple size={10} aria-hidden />
            )}
          </button>
          <button
            type="button"
            className={cx(
              SIDEBAR_RECENTS_ACTION_BTN,
              "text-puntt-neutral-gray-11 hover:bg-puntt-red-9 hover:text-base-white",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRequestDelete(project._id);
            }}
            aria-label="Delete project"
            data-testid="sidebar-ask-puntt-delete-recent"
          >
            <Trash size={10} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Meta line for a chat row in the combined Recents stream. Always leads with
 * the "Chat" kind label so a chat is visually distinguishable from a project
 * row (which leads with "Project") now that both kinds share one list, then
 * appends owner (shared chats only) + relative time:
 * "Chat · 2 hours ago" / "Chat · Kevin Johnson · 2 hours ago" / "Chat".
 */
function formatChatMeta(
  updatedAt?: string | null,
  ownerName?: string | null,
): string {
  let when: string | null = null;
  if (updatedAt) {
    const date = new Date(updatedAt);
    if (!Number.isNaN(date.getTime())) {
      when = formatDistanceToNow(date, { addSuffix: true });
    }
  }
  const parts = ["Chat", ownerName?.trim() || null, when].filter(
    (part): part is string => Boolean(part),
  );
  return parts.join(" · ");
}

// Shared explicit footprint for the three footer trigger cards (settings,
// login, account). They MUST share the same outer dimensions: in the
// collapsed icon rail (`calc(3.5rem + 1px)` = 57px) the SidebarFooter `p-4`
// leaves a 25px-wide button area, so any card wider than 24px (e.g. the
// previous `size-7` = 28px) overflows and is visibly clipped on the right
// by `SidebarMenuButton`'s `overflow-hidden`. Keeping all three cards at
// `size-6` (24px) matches the Radix Avatar `size="1"` footprint, fits
// cleanly in both collapsed and expanded rail widths, and gives the three
// cards a consistent vertical rhythm.
export const SIDEBAR_TRIGGER_CARD_SIZE_CLASS = "size-6";

// Footer action buttons (Collapse, Invite, Settings, Log In, Account) keep the
// ORIGINAL compact layout — same 24px icon footprint, same `!p-0` button and
// `px-1.5 py-1` label paddings, same icon glyph sizes — with exactly two
// changes from the earlier bordered-icon-card treatment:
//   1. the single grey hover highlight now lives on the WHOLE button, so it
//      spans the full width (icon + text) instead of only the label; and
//   2. the leading glyph carries NO border / card / shadow of its own.
// Hover / open highlight uses the SAME translucent `grayA` tokens as the upper
// nav rows (see `navLinkClasses`: hover `grayA-2`, active `grayA-3`). The footer
// previously used the solid `gray-3` token, whose opaque fill repainting behind
// the anti-aliased glyph made the icon look like it nudged ~1px on hover — the
// translucent nav token does not. Matching the tokens keeps the footer cohesive
// with the upper sections.
// `transform-none` pixel-LOCKS the icon: only the translucent background box
// may change on hover — the glyph itself can never be nudged by a stray
// transform (matches the row +/- treatment, "only the box highlights"). The
// hover token is the SAME translucent `grayA` used by the upper nav rows (an
// opaque fill repainting behind the anti-aliased glyph is what made it look
// like the icon nudged ~1px). In the collapsed rail the button stretches to
// the full rail width and centers its glyph so the Invite / Settings / Account
// icons line up on a single vertical axis.
export const SIDEBAR_FOOTER_BUTTON_CLASS =
  "group/menu-button !p-0 rounded-md transition-colors transform-none hover:bg-puntt-neutral-grayA-2 group-data-[state=open]/dropdown-menu:bg-puntt-neutral-grayA-3 group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-center";

// Borderless 24px icon slot for the neutral-grey footer glyphs. `mx-auto` in
// the collapsed rail is a belt-and-suspenders centering guarantee so the glyph
// sits dead-center regardless of the wrapper (the bare Invite button vs the
// DropdownMenu-wrapped Settings/Account triggers).
export const SIDEBAR_FOOTER_GLYPH_CLASS =
  "flex size-6 shrink-0 items-center justify-center group-data-[collapsible=icon]:mx-auto";

// Label wrapper: keeps the original text padding but no longer carries its own
// hover highlight (the button does now). Hidden in the collapsed icon rail.
export const SIDEBAR_FOOTER_LABEL_CLASS =
  "px-1.5 py-1 group-data-[collapsible=icon]:hidden";

// useAutoCollapseSidebar + the FOCUS_PATH_FRAGMENTS list live in
// hooks/useAutoCollapseSidebar.ts so the category-transition logic is
// testable in isolation. AppSidebar consumes the hook directly.

export function AppSidebar() {
  const posthog = usePostHog();
  const analytics = useAnalytics("AppSidebar");
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // New Project starts a first-class project; New Chat opens the Ask Puntt
  // home composer (the blank-chat surface). Both live next to their top-level
  // nav rows.
  const { startNewProjectChat, isPending: isStartingProject } =
    useStartNewProjectChat("ask_puntt_sidebar");
  const user = useAppSelector((state) => state.auth.value);
  // The auth-layout route already guards against rendering the sidebar when
  // `state.auth.value` is null (redirects to logout). Use optional chaining
  // rather than asserting with `!` in case a React batching edge lands us here
  // mid-logout.
  const c = resolveAvatarColors(user ?? undefined);
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  // Demo-aware auto-collapse — every tenant benefits from the focus
  // surface auto-collapse, not just demo. Real customers' chat / ticket
  // detail pages are also dense enough to want the rail.
  useAutoCollapseSidebar();

  return (
    <SidebarProvider
      name="app"
      open={sidebarOpen}
      onOpenChange={(open) => {
        analytics.capture(`sidebar_${open ? "opened" : "closed"}`);
        dispatch(setDrawerOpen({ app: open }));
      }}
    >
      <Sidebar data-testid="app-sidebar">
        <AppSidebarHeader />

        <SidebarContent data-testid="app-sidebar-content">
          <Flex direction="column" className="min-h-0 flex-1 overflow-hidden">
            <SidebarGroup className="shrink-0 pb-0">
              <SidebarMenu>
                <SidebarMenuItem
                  data-testid="sidebar-menu-item-dashboard"
                  data-auth-trigger="dashboard"
                  hidden={!canAccessPunttDashboard()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={indexRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <House
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-data-[state=delayed-open]/menu-item-tooltip:translate-y-0 group-data-[state=instant-open]/menu-item-tooltip:translate-y-0 group-data-[state=delayed-open]/menu-item-tooltip:opacity-100 group-data-[state=instant-open]/menu-item-tooltip:opacity-100"
                            >
                              Home
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <House size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        Home
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-menu-item-ask-puntt"
                  data-auth-trigger="ask-puntt"
                  hidden={!canAccessAskPuntt()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={askPunttRoute.to}
                      className={navLinkClasses}
                      // Exact match only: on a project/chat sub-route the rail
                      // already marks the active list row, so the parent "Ask
                      // Puntt" item must NOT also light up (one clear active
                      // state, not two).
                      activeOptions={{ exact: true }}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <ChatCircle
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-data-[state=delayed-open]/menu-item-tooltip:translate-y-0 group-data-[state=instant-open]/menu-item-tooltip:translate-y-0 group-data-[state=delayed-open]/menu-item-tooltip:opacity-100 group-data-[state=instant-open]/menu-item-tooltip:opacity-100"
                            >
                              Ask Puntt
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <ChatCircle
                            size={18}
                            color="rgb(var(--puntt-blue-12))"
                          />
                        </Box>
                        Ask Puntt
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-nav-projects"
                  hidden={!canAccessAskPuntt()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={askPunttProjectsRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <Kanban
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-data-[state=delayed-open]/menu-item-tooltip:translate-y-0 group-data-[state=instant-open]/menu-item-tooltip:translate-y-0 group-data-[state=delayed-open]/menu-item-tooltip:opacity-100 group-data-[state=instant-open]/menu-item-tooltip:opacity-100"
                            >
                              Projects
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <Kanban size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        Projects
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                  {/* New Project sits over the right edge of the row; the
                      button is on top of the NavLink so its click starts a
                      project instead of navigating. Vertically centered to the
                      row (flex h-full items-center). Expanded rail only. */}
                  <div className="absolute right-2 top-1/2 flex h-full -translate-y-1/2 items-center group-data-[collapsible=icon]:hidden">
                    <Button
                      size="1"
                      variant="surface"
                      color="gray"
                      style={{ zIndex: 2 }}
                      disabled={isStartingProject}
                      onClick={() =>
                        void startNewProjectChat("sidebar_projects_plus")
                      }
                      aria-label="New project"
                      data-testid="sidebar-ask-puntt-new-project"
                    >
                      <Plus size={14} aria-hidden="true" />
                      New Project
                    </Button>
                  </div>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-nav-chats"
                  hidden={!canAccessAskPuntt()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={askPunttChatsRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <ChatText
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-data-[state=delayed-open]/menu-item-tooltip:translate-y-0 group-data-[state=instant-open]/menu-item-tooltip:translate-y-0 group-data-[state=delayed-open]/menu-item-tooltip:opacity-100 group-data-[state=instant-open]/menu-item-tooltip:opacity-100"
                            >
                              Chats
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <ChatText
                            size={18}
                            color="rgb(var(--puntt-blue-12))"
                          />
                        </Box>
                        Chats
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                  {/* New Chat opens the Ask Puntt home composer (blank chat).
                      Vertically centered to the row (flex h-full items-center).
                      Expanded rail only. */}
                  <div className="absolute right-2 top-1/2 flex h-full -translate-y-1/2 items-center group-data-[collapsible=icon]:hidden">
                    <Button
                      size="1"
                      variant="surface"
                      color="gray"
                      style={{ zIndex: 2 }}
                      onClick={() => navigate({ to: askPunttRoute.to })}
                      aria-label="New Chat"
                      data-testid="sidebar-ask-puntt-new-chat"
                    >
                      <Plus size={14} aria-hidden="true" />
                      New Chat
                    </Button>
                  </div>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>

            {/*
              Combined Recents list: recent projects + chats merged and
              sorted by updatedAt. Fills the rail when Documents is hidden;
              otherwise sits content-sized above the Documents group.
            */}
            <AskPunttRecents />

            {showDocumentsInSidebar() && (
              <SidebarGroup className="shrink-0 pb-0">
                <SidebarMenu>
                  <SidebarMenuItem
                    data-testid="sidebar-menu-item-projects"
                    data-auth-trigger="projects"
                    hidden={canAccessPunttDashboard()}
                  >
                    <Link
                      size="3"
                      weight="medium"
                      className="puntt-link"
                      asChild
                    >
                      <NavLink
                        to={ticketsRoute.to}
                        className={navLinkClasses}
                        activeProps={{
                          className: activeNavLinkClasses,
                        }}
                      >
                        <SidebarMenuButton
                          tooltip={
                            <Flex align="center" className="gap-3">
                              <Kanban
                                size={18}
                                color="rgb(var(--puntt-blue-12))"
                              />
                              <Text
                                size="3"
                                className="puntt-link translate-y-1/2 opacity-0 duration-150 group-data-[state=delayed-open]/menu-item-tooltip:translate-y-0 group-data-[state=instant-open]/menu-item-tooltip:translate-y-0 group-data-[state=delayed-open]/menu-item-tooltip:opacity-100 group-data-[state=instant-open]/menu-item-tooltip:opacity-100"
                              >
                                Documents
                              </Text>
                            </Flex>
                          }
                        >
                          <Box className="shrink-0 p-1">
                            <Kanban
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                          </Box>
                          Documents
                        </SidebarMenuButton>
                      </NavLink>
                    </Link>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>
            )}

            {showDocumentsInSidebar() && <FolderTreeView />}
          </Flex>
        </SidebarContent>

        <SidebarFooter data-testid="app-sidebar-footer">
          <SidebarMenu>
            {/* Gate by conditional render, not the `hidden` attribute, so
                Restricted (CATALYST_REQUESTER) users get NO invite markup in
                the DOM at all (the gating E2E spec asserts toHaveCount(0)). */}
            {canInvitePeople() && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  type="button"
                  onClick={() => setIsInviteOpen(true)}
                  aria-label="Invite People"
                  data-testid="sidebar-invite-people-trigger"
                  // Pixel-lock the icon on hover. The collapsed hover flyout
                  // overlays a DUPLICATE icon on top of the resting button
                  // glyph, pulled left by the shared nav default `-ml-10`
                  // (-40px). That default is calibrated for the wider primary
                  // nav buttons; the footer's collapsed button is only ~41px
                  // wide (57px rail − `px-2`), and because its glyph is
                  // `justify-center`'d the resting icon sits at button.left +
                  // W/2 − 7. With a -40px pull the flyout's icon lands 0.50px
                  // LEFT of that, so the icon appears to nudge left the instant
                  // the tooltip opens (verified in headless Chromium against
                  // the real Radix tooltip: glyph self-delta 0.00px, flyout
                  // icon −0.50px). A -39.5px pull (= −(W/2 + 19)) registers the
                  // flyout icon exactly over the resting glyph → 0.00px delta.
                  tooltipClassName="!-ml-[39.5px]"
                  tooltip={
                    <SidebarTooltipLabel
                      icon={
                        <UserPlus size={14} color="rgb(var(--puntt-blue-12))" />
                      }
                      label="Invite People"
                    />
                  }
                  className={SIDEBAR_FOOTER_BUTTON_CLASS}
                >
                  <span
                    aria-hidden
                    data-testid="sidebar-invite-people-trigger-card"
                    className={SIDEBAR_FOOTER_GLYPH_CLASS}
                  >
                    <UserPlus
                      size={14}
                      className="text-puntt-neutral-gray-11"
                    />
                  </span>

                  <Flex width="100%" className={SIDEBAR_FOOTER_LABEL_CLASS}>
                    <Text
                      color="gray"
                      size="1"
                      weight="medium"
                      className="flex-1 text-left"
                    >
                      Invite People
                    </Text>
                  </Flex>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {/* Partial-width grey separator that visually splits the invite
                action (the ADD control) from the Settings/Account group below.
                Only rendered when the invite row exists (nothing to separate
                otherwise). The negative `-my-1` pulls the line INTO the
                surrounding `gap-2` flex gaps so the Invite/Settings group reads
                tighter together (was `my-1`, which added 4px on top of the 8px
                gap and left the line floating). It now renders in BOTH states:
                expanded uses the full `mx-3` inset width; the collapsed icon
                rail (~41px wide) gets a short, centered 24px line (`mx-auto`
                overrides the `mx-3` inset, `w-6` matches the footer glyph box)
                so the separator still reads in the narrow rail instead of
                disappearing. */}
            {canInvitePeople() && (
              <div
                role="separator"
                aria-orientation="horizontal"
                data-testid="account-menu-divider"
                className="-my-1 mx-3 h-px bg-puntt-neutral-gray-6 group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:w-6"
              />
            )}

            <SidebarMenuItem
              hidden={
                !canAccessKnowledgeDatabase() &&
                !canManageBrandGuidelines() &&
                !canManageUsers() &&
                !isMeaningfulGigsOrDemo()
              }
            >
              <SidebarFooterMenu
                open={isSettingsOpen}
                onOpenChange={setIsSettingsOpen}
                triggerTestId="sidebar-settings-trigger"
                ariaLabel="Settings"
                trigger={
                  <SidebarMenuButton className={SIDEBAR_FOOTER_BUTTON_CLASS}>
                    <span
                      aria-hidden
                      data-testid="sidebar-settings-trigger-card"
                      className={SIDEBAR_FOOTER_GLYPH_CLASS}
                    >
                      <Gear size={14} className="text-puntt-neutral-gray-11" />
                    </span>

                    <Flex width="100%" className={SIDEBAR_FOOTER_LABEL_CLASS}>
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        Settings
                      </Text>

                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                }
              >
                <DropdownMenu.Item
                  hidden={!isMeaningfulGigsRole()}
                  data-testid="sidebar-settings-enterprise"
                >
                  <NavLink
                    data-testid="app-sidebar-nav-link-enterprise-settings"
                    to={enterpriseSettingsRoute.to}
                    className="w-full"
                    onClick={() => setIsSettingsOpen(false)}
                  >
                    Enterprise
                  </NavLink>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  hidden={!showDocumentsInSettings()}
                  data-testid="sidebar-settings-documents"
                >
                  <NavLink
                    data-testid="app-sidebar-nav-link-documents"
                    to={ticketsRoute.to}
                    className="w-full"
                    onClick={() => setIsSettingsOpen(false)}
                  >
                    Documents
                  </NavLink>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  hidden={!isMeaningfulGigsOrDemo()}
                  data-testid="sidebar-settings-roi"
                >
                  <NavLink
                    data-testid="app-sidebar-nav-link-roi"
                    to={roiRoute.to}
                    className="w-full"
                    onClick={() => setIsSettingsOpen(false)}
                  >
                    ROI Dashboard
                  </NavLink>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  hidden={!canManageBrandGuidelines()}
                  data-testid="sidebar-settings-knowledge"
                >
                  <NavLink
                    data-testid="app-sidebar-nav-link-416"
                    to={knowledgeSettingRoute.to}
                    className="w-full"
                    onClick={() => setIsSettingsOpen(false)}
                  >
                    Knowledge Settings
                  </NavLink>
                </DropdownMenu.Item>
                <DropdownMenu.Item hidden={!canAccessKnowledgeDatabase()}>
                  <NavLink
                    data-testid="app-sidebar-nav-link-425"
                    to={knowledgeDatabaseRoute.to}
                    search={{ pageSize: 100 }}
                    className="w-full"
                    onClick={() => setIsSettingsOpen(false)}
                  >
                    Knowledge Database
                  </NavLink>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  hidden={!canManageUsers()}
                  data-testid="sidebar-settings-integration"
                >
                  <NavLink
                    data-testid="app-sidebar-nav-link-438"
                    to={integrationSettingsRoute.to}
                    className="w-full"
                    onClick={() => setIsSettingsOpen(false)}
                  >
                    Integration Settings
                  </NavLink>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  hidden={!canManageUsers()}
                  data-testid="sidebar-settings-manage-users"
                >
                  <NavLink
                    data-testid="app-sidebar-nav-link-450"
                    to={usersRoute.to}
                    className="w-full"
                    onClick={() => setIsSettingsOpen(false)}
                  >
                    Manage Users
                  </NavLink>
                </DropdownMenu.Item>
                {canAccessAskPuntt() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-notifications">
                    <NavLink
                      data-testid="app-sidebar-nav-link-notifications"
                      to={notificationsSettingsRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Notifications
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                <DropdownMenu.Item hidden={!canManageUsers()}>
                  <NavLink
                    data-testid="app-sidebar-nav-link-459"
                    to={archivedUsersRoute.to}
                    className="w-full"
                    onClick={() => setIsSettingsOpen(false)}
                  >
                    Archived Users
                  </NavLink>
                </DropdownMenu.Item>
                {/*
                    Demo Settings shortcut — Vitalora sales demo only
                    (slug `vitalora`). Other isDemo sandboxes / MG prod
                    tenants must not see this chrome.

                    Conditionally mount (do NOT use `hidden=`): Tailwind
                    preflight strips UA `[hidden]{display:none!important}`,
                    so Radix `rt-BaseMenuItem { display: flex }` keeps the
                    row visible even when the Vitalora gate is false —
                    which is why MG staff still saw "Demo Settings" on
                    the Meaningful Gigs enterprise.
                  */}
                {isVitaloraDemoEnterprise() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-demo">
                    {/*
                      `to` is the literal string `/settings`, not
                      `settingsRoute.to`. Importing the settings route
                      module here would transitively pull in every demo
                      card + auth-layout + the eager `new QueryClient()`
                      at `src/utils/queryClient.tsx`, which crashes
                      `AppSidebar.test.tsx` at import time (its
                      `vi.mock("@tanstack/react-query")` doesn't export
                      `QueryClient`). The string form has the same
                      runtime behaviour for NavLink without the import
                      bloat.
                    */}
                    <NavLink
                      data-testid="app-sidebar-nav-link-demo-settings"
                      to="/settings"
                      hash="demo-settings"
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Demo Settings
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
              </SidebarFooterMenu>
            </SidebarMenuItem>

            <SidebarMenuItem
              hidden={!isNil(user?.email)}
              data-auth-trigger="login-signup-button"
            >
              <SidebarMenuButton
                // Same pixel-lock as the Invite button: the collapsed hover
                // flyout overlays a DUPLICATE icon on the resting glyph, and the
                // shared nav default `-ml-10` (-40px) lands it 0.50px LEFT on
                // the ~41px footer button. -39.5px (= -(W/2 + 19)) registers the
                // flyout icon exactly over the glyph (headless-verified 0.00px).
                tooltipClassName="!-ml-[39.5px]"
                tooltip={
                  <SidebarTooltipLabel
                    icon={
                      <UserCircle size={14} color="rgb(var(--puntt-blue-12))" />
                    }
                    label="Log In or Sign Up"
                  />
                }
                className={SIDEBAR_FOOTER_BUTTON_CLASS}
              >
                <span
                  aria-hidden
                  data-testid="sidebar-login-trigger-card"
                  className={SIDEBAR_FOOTER_GLYPH_CLASS}
                >
                  <UserCircle
                    size={14}
                    className="text-puntt-neutral-gray-11"
                  />
                </span>

                <Flex width="100%" className={SIDEBAR_FOOTER_LABEL_CLASS}>
                  <Text
                    color="gray"
                    size="1"
                    weight="medium"
                    className="flex-1 whitespace-nowrap text-left"
                  >
                    Log In or Sign Up
                  </Text>
                </Flex>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem hidden={isNil(user?.email)}>
              <SidebarFooterMenu
                open={isAccountOpen}
                onOpenChange={setIsAccountOpen}
                triggerTestId="sidebar-account-trigger"
                ariaLabel="Account"
                contentClassName="min-w-[220px]"
                trigger={
                  <SidebarMenuButton className={SIDEBAR_FOOTER_BUTTON_CLASS}>
                    {/* AvatarWithInitials (not a raw Radix Avatar) so the
                        user's persisted Edit-Profile colors drive the small
                        rail "initials" circle — `size={6}` keeps the 24px
                        footprint, and the `name`-derived initials fall back to
                        the outlined Icon.User silhouette when there's no name. */}
                    <AvatarWithInitials
                      size={6}
                      name={user?.name ?? ""}
                      avatar={user?.avatar}
                      colorKey={c.colorKey}
                      backgroundColor={c.backgroundColor}
                      textColor={c.textColor}
                      data-testid="sidebar-account-trigger-card"
                      className="size-6 shrink-0 group-data-[collapsible=icon]:mx-auto"
                    />

                    <Flex width="100%" className={SIDEBAR_FOOTER_LABEL_CLASS}>
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        Account
                      </Text>

                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                }
              >
                {/* Header area */}
                <div className="flex flex-col items-center py-4">
                  <AvatarWithInitials
                    size={16}
                    name={user?.name ?? ""}
                    avatar={user?.avatar}
                    colorKey={c.colorKey}
                    backgroundColor={c.backgroundColor}
                    textColor={c.textColor}
                  />
                  <span className="mt-2 text-center text-lg font-semibold">
                    {user?.name}
                  </span>
                  <span className="text-gray-500 mt-1 text-center text-sm">
                    {user?.email}
                  </span>
                </div>
                <div className="border-gray-200 my-2 border-t" />
                <DropdownMenu.Item
                  onClick={() => {
                    setIsEditProfileOpen(true);
                    setIsAccountOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  Edit Profile
                </DropdownMenu.Item>
                <div className="border-gray-200 my-2 border-t" />
                <DropdownMenu.Item
                  onClick={() => {
                    dispatch(logout());
                    posthog.reset();
                    setIsAccountOpen(false);
                  }}
                  className="text-red-600 cursor-pointer"
                  data-testid="sidebar-account-logout"
                >
                  Log Out
                </DropdownMenu.Item>
              </SidebarFooterMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail data-testid="app-sidebar-rail" />
      </Sidebar>

      <EditProfileDialog
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
      />

      <InvitePeopleDialog open={isInviteOpen} onOpenChange={setIsInviteOpen} />
    </SidebarProvider>
  );
}

/**
 * Footer dropdown (Settings / Account) whose menu reveals on HOVER while the
 * rail is collapsed.
 *
 * In the collapsed rail these are icon-only buttons. A tooltip there could only
 * repeat the single word ("Settings" / "Account") — which the dropdown already
 * spells out in full — so instead of a tooltip plus a required click, hovering
 * the icon opens the menu itself (no click, no tooltip). A short close delay
 * bridges the gap between the trigger and the portalled content so the menu
 * doesn't snap shut while the pointer travels into it. The collapsed hover-open
 * is also non-modal so it doesn't trap focus or scroll-lock the page.
 *
 * When the rail is EXPANDED the menu keeps its normal click-to-open + modal
 * behaviour: the full-width row is a deliberate click target, and hover-opening
 * it as the pointer merely passes by would be noisy. Only the Invite (Add)
 * footer item keeps a tooltip and a click (it opens a dialog, not a menu).
 */
function SidebarFooterMenu({
  open,
  onOpenChange,
  triggerTestId,
  ariaLabel,
  trigger,
  contentClassName,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerTestId: string;
  ariaLabel: string;
  trigger: ReactNode;
  contentClassName?: string;
  children: ReactNode;
}) {
  // `useSidebar` only resolves inside the provider; this component is rendered
  // within the footer (under <SidebarProvider>), so reading the rail state here
  // is safe even though the parent AppSidebar (which renders the provider)
  // cannot.
  const { open: railOpen } = useSidebar();
  const collapsed = !railOpen;
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  // Clear any pending close on unmount so a late timer can't call
  // `onOpenChange` after this menu is gone.
  useEffect(() => cancelClose, [cancelClose]);

  const openNow = useCallback(() => {
    cancelClose();
    onOpenChange(true);
  }, [cancelClose, onOpenChange]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    // ~150ms grace so moving from the icon into the portalled menu isn't read
    // as "left the menu" and closed mid-travel.
    closeTimer.current = setTimeout(() => onOpenChange(false), 150);
  }, [cancelClose, onOpenChange]);

  const hoverTriggerProps = collapsed
    ? { onMouseEnter: openNow, onMouseLeave: scheduleClose }
    : {};
  const hoverContentProps = collapsed
    ? { onMouseEnter: cancelClose, onMouseLeave: scheduleClose }
    : {};

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={onOpenChange}
      modal={!collapsed}
    >
      <DropdownMenu.Trigger
        // `w-full` so the nested SidebarMenuButton spans the whole rail and its
        // glyph centers on the SAME vertical axis as the bare Invite button
        // (which is already a full-width footer button). Without this the
        // wrapped Settings/Account triggers and the unwrapped Invite trigger
        // could center on different widths and look misaligned in the rail.
        className="group/dropdown-menu w-full"
        data-testid={triggerTestId}
        aria-label={ariaLabel}
        {...hoverTriggerProps}
      >
        {trigger}
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        side="right"
        className={contentClassName}
        {...hoverContentProps}
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

/**
 * Collapsed-rail hover label for the footer action items.
 *
 * The primary nav items (ROI / Home / Ask Puntt / Documents) already pass a
 * `tooltip` to `SidebarMenuButton`, so when the rail is collapsed to icons a
 * hover reveals the item's name. The Invite (Add) footer item also uses one so
 * its icon-only collapsed button stays discoverable on hover. (Settings and
 * Account instead open their menu on hover — see `SidebarFooterMenu`.)
 */
export function SidebarTooltipLabel({
  icon,
  label,
}: {
  icon: ReactNode;
  label: string;
}) {
  // Footer items (Invite / Settings / Log In / Account) are `size="1"` rows, so
  // their collapsed-rail tooltip must match that compact scale — NOT the larger
  // `size="3"` tooltip the primary nav items (ROI / Home / Ask Puntt) use.
  return (
    <Flex align="center" className="gap-2">
      {icon}
      <Text
        size="1"
        className="puntt-link translate-y-1/2 opacity-0 duration-150 group-data-[state=delayed-open]/menu-item-tooltip:translate-y-0 group-data-[state=instant-open]/menu-item-tooltip:translate-y-0 group-data-[state=delayed-open]/menu-item-tooltip:opacity-100 group-data-[state=instant-open]/menu-item-tooltip:opacity-100"
      >
        {label}
      </Text>
    </Flex>
  );
}

/**
 * Always-visible header affordance for collapsing / expanding the rail.
 *
 * The sidebar can also be toggled via the draggable `SidebarRail` (edge-only),
 * but that does not read as an obvious "open / close the nav" control. This
 * icon button lives at the top of the sidebar: inline at the right of the
 * header when the rail is open, and floating over the right edge (overlapping
 * content) when the rail is collapsed to icons. The directional arrow shows
 * which way the rail will move, and the tooltip surfaces the full label in
 * both states. It must live in its own component because `useSidebar` can only
 * be read inside `SidebarProvider`, which `AppSidebar` renders.
 */
function SidebarCollapseToggle() {
  const { open, toggleSidebar } = useSidebar();
  const label = open ? "Collapse Sidebar" : "Expand Sidebar";

  return (
    <Tooltip content={label} side="right">
      <IconButton
        type="button"
        // Open: a plain ghost icon that sits inline at the right of the header.
        // Collapsed: a grey-outlined `surface` pill so it reads as the same
        // "collapse" control as the grey rail border. (Avoid `ghost` when
        // collapsed — Radix ghost adds alignment margins that break the
        // absolute centring.)
        variant={open ? "ghost" : "surface"}
        color="gray"
        radius="full"
        // Collapsed: the smaller `1` keeps the white pill hugging the 16px icon
        // (a `2` left a fat ring of empty white). Both states share the same
        // 16px arrow, so the open (bare ghost) and collapsed (ringed) toggles
        // read as the same-sized control.
        size={open ? "2" : "1"}
        onClick={toggleSidebar}
        aria-label={label}
        data-testid="sidebar-collapse-trigger"
        className={cx(
          "shrink-0 text-puntt-neutral-gray-11",
          // Collapsed: straddle the rail's right border so the pill is centred
          // ON the grey divider line (translate-x-1/2 = half its own width past
          // the edge) and vertically centred in the header. The outline is an
          // inset `ring` in the SAME token as the rail (`puntt-neutral-gray-6`)
          // so it reads as one connected control. A plain CSS `border` is
          // invisible here — preflight is disabled (no `border-style: solid`)
          // and Radix's `.rt-reset` zeroes the IconButton's `border-width`; a
          // ring is a box-shadow, so it survives both. `!ring-1` beats the
          // `surface` variant's own faint box-shadow and still composes with
          // `shadow-sm`.
          // On hover the ring thickens and shifts to `gray-5` — exactly how the
          // rail line behaves (persistent `gray-6`, hover highlight `gray-5`) —
          // so the pill and the line read as one cohesive control. `hover:!ring-2`
          // needs `!` to beat the important base `!ring-1`.
          !open &&
            "absolute right-0 top-1/2 z-30 -translate-y-1/2 translate-x-1/2 bg-base-white shadow-sm !ring-1 ring-inset ring-puntt-neutral-gray-6 hover:!ring-2 hover:ring-puntt-neutral-gray-5",
        )}
      >
        {open ? <ArrowLineLeft size={16} /> : <ArrowLineRight size={16} />}
      </IconButton>
    </Tooltip>
  );
}

function AppSidebarHeader() {
  const user = useAppSelector((state) => state.auth.value);
  const { open } = useSidebar();

  return (
    <SidebarHeader
      data-testid="app-sidebar-header"
      data-auth-trigger="sidebar-header"
      // Positioning context for the collapsed-rail floating toggle so it
      // anchors to the FULL-WIDTH header, not the shrink-to-logo content row.
      className="relative"
    >
      {open ? (
        <Flex gap="3" align="center" width="100%">
          <NavLink
            to={askPunttRoute.to}
            className="shrink-0"
            aria-label="Go to Ask Puntt"
            data-testid="sidebar-logo-link"
          >
            <Wordmark className="h-4 max-w-max" />
          </NavLink>

          {/* The switcher (or, for single-enterprise users, the plain company
              name) fills the space between the logo and the collapse toggle. */}
          {!isNil(user?.email) ? (
            <Flex align="center" className="min-w-0 flex-1">
              <EnterpriseSwitcher />
            </Flex>
          ) : (
            <div className="flex-1" />
          )}

          <SidebarCollapseToggle />
        </Flex>
      ) : (
        <>
          <NavLink
            to={askPunttRoute.to}
            aria-label="Go to Ask Puntt"
            data-testid="sidebar-logo-link"
          >
            <Logo className="size-6" />
          </NavLink>

          <SidebarCollapseToggle />
        </>
      )}
    </SidebarHeader>
  );
}

const SIDEBAR_RECENTS_LIMIT = 10;

const toRecentTime = (value: string | Date | null | undefined) =>
  value ? new Date(value).getTime() : 0;

function AskPunttRecents() {
  const { data: projectsData } = useAskPunttProjects({
    scope: "all",
    includeReviewState: true,
    refetchInterval: ASK_PUNTT_UNREAD_LIST_POLL_MS,
  });
  const { data: chatsData } = useAskPunttChats({
    refetchInterval: ASK_PUNTT_UNREAD_LIST_POLL_MS,
  });
  const { open } = useSidebar();
  const { matches, location } = useRouterState();
  const navigate = useNavigate();
  const deleteProjectMutation = useDeleteAskPunttProject();
  const deleteChatMutation = useDeleteAskPunttChat();
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const [projectIdToDelete, setProjectIdToDelete] = useState<string | null>(
    null,
  );
  const [chatIdToDelete, setChatIdToDelete] = useState<string | null>(null);

  if (!canAccessAskPuntt() || !open) return null;

  const projects = projectsData?.projects ?? [];
  const chats = (chatsData?.chats ?? []).filter(
    (c) => c.workflowType !== "project",
  );

  // One merged, recency-sorted stream of projects + chats. Both carry
  // updatedAt; rows missing a timestamp sort last.
  const recents = [
    ...projects.map((project) => ({
      kind: "project" as const,
      id: project._id,
      time: toRecentTime(project.updatedAt),
      project,
    })),
    ...chats.map((chat) => ({
      kind: "chat" as const,
      id: chat._id,
      time: toRecentTime(chat.updatedAt),
      chat,
    })),
  ]
    .sort((a, b) => b.time - a.time)
    .slice(0, SIDEBAR_RECENTS_LIMIT);

  if (recents.length === 0) return null;

  // Param-based active match (basepath-immune on the epic env): match the
  // route param, not a hardcoded pathname string.
  const isActiveProject = (projectId: string) =>
    matches.some(
      (m) => (m.params as { projectId?: string })?.projectId === projectId,
    );

  // Only the project's OWNER may delete it (the backend filters on ownerId
  // anyway; gating the affordance keeps the UI honest). Treat unresolved auth
  // as not-owner so we never flash a delete button on someone else's project.
  const canDeleteProject = (project: {
    role?: "owner" | "member";
    ownerId?: string;
  }) =>
    project.role === "owner" ||
    (currentUserId != null &&
      project.ownerId != null &&
      project.ownerId === currentUserId);

  function handleDeleteProject(projectId: string) {
    const wasViewing = isActiveProject(projectId);
    deleteProjectMutation.mutate(projectId, {
      onSuccess: () => {
        setProjectIdToDelete(null);
        if (wasViewing) {
          navigate({ to: askPunttRoute.to });
        }
      },
    });
  }

  function handleDeleteChat(chatId: string) {
    const chat = chats.find((c) => c._id === chatId);
    const currentPath = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const isViewing =
      chat != null
        ? isViewingAskPunttChat(chat, currentPath, searchParams)
        : currentPath.includes(`/ask-puntt/${chatId}`);
    deleteChatMutation.mutate(chatId, {
      onSuccess: () => {
        setChatIdToDelete(null);
        if (isViewing) {
          if (chat?.projectId && chat.projectChatKind === "personal") {
            navigate({
              to: campaignRoute.to,
              params: { projectId: chat.projectId },
              search: campaignSearch(),
            });
          } else {
            navigate({ to: askPunttRoute.to });
          }
        }
      },
    });
  }

  const projectToDelete = projects.find((p) => p._id === projectIdToDelete);
  const chatToDelete = chats.find((c) => c._id === chatIdToDelete);

  return (
    <>
      <nav
        aria-label="Recents"
        data-testid="sidebar-ask-puntt-recents"
        className="flex min-h-0 flex-1 flex-col overflow-hidden p-2"
      >
        <div
          className={`shrink-0 ${SIDEBAR_SECTION_HEADER_INDENT} py-1.5 pr-2`}
          data-testid="sidebar-ask-puntt-recents-header"
        >
          <Text size="2" weight="bold" className="truncate px-0">
            Recents
          </Text>
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto"
          data-testid="sidebar-ask-puntt-recents-list"
        >
          {recents.map((item) => {
            if (item.kind === "project") {
              const project = item.project;
              return (
                <RecentProjectRow
                  key={`project-${project._id}`}
                  project={project}
                  active={isActiveProject(project._id)}
                  canModify={canDeleteProject(project)}
                  onRequestDelete={setProjectIdToDelete}
                />
              );
            }

            const chat = item.chat;
            // Treat as own until auth resolves so the delete button doesn't
            // briefly disappear from own chats before currentUserId loads.
            const isOwnChat =
              currentUserId === undefined || chat.userId === currentUserId;
            const active = isAskPunttChatNavActive(
              chat,
              matches,
              location.search,
            );
            const metaText = formatChatMeta(
              chat.updatedAt,
              isOwnChat ? null : chat.ownerName,
            );
            return (
              <div
                key={`chat-${chat._id}`}
                className={cx(
                  `group/chat-item relative flex w-full items-center gap-4 rounded-md py-1.5 ${SIDEBAR_ITEM_INDENT} pr-2 transition-colors hover:bg-puntt-neutral-gray-3`,
                  active && SIDEBAR_ACTIVE_ROW_CLASS,
                )}
                data-testid="sidebar-ask-puntt-recent-item"
                data-kind="chat"
                data-chat-id={chat._id}
                data-active={active ? "true" : "false"}
              >
                {active ? <SidebarActiveBar /> : null}
                <Link asChild size="2" className="flex-1 overflow-hidden">
                  <NavLink
                    {...askPunttChatNavLink(chat)}
                    className="w-full overflow-hidden"
                  >
                    <span className="flex items-center gap-2">
                      <SidebarRowTitle
                        name={chat.title}
                        testId="sidebar-ask-puntt-recent-title"
                      />
                      {chat.hasUnread ? (
                        <UnreadDot
                          testId={`sidebar-ask-puntt-recent-unread-${chat._id}`}
                          ariaLabel="Unread"
                        />
                      ) : null}
                    </span>
                    {metaText ? (
                      <span
                        className={SIDEBAR_ITEM_META_CLASS}
                        style={SIDEBAR_ITEM_META_STYLE}
                        data-testid="sidebar-ask-puntt-recent-meta"
                      >
                        <span className="truncate">{metaText}</span>
                      </span>
                    ) : null}
                  </NavLink>
                </Link>
                {isOwnChat ? (
                  <div className={SIDEBAR_RECENTS_ACTIONS_WRAP}>
                    <button
                      type="button"
                      className={cx(
                        SIDEBAR_RECENTS_ACTION_BTN,
                        "text-puntt-neutral-gray-11 hover:bg-puntt-red-9 hover:text-base-white",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setChatIdToDelete(chat._id);
                      }}
                      aria-label="Delete chat"
                      data-testid="sidebar-ask-puntt-delete-recent"
                    >
                      <Trash size={10} aria-hidden />
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </nav>

      <Dialog.Root
        open={projectIdToDelete != null}
        onOpenChange={(open) => {
          if (!open) setProjectIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Delete project?</Dialog.Title>
          <Dialog.Description>
            Are you sure you want to delete{" "}
            {projectToDelete ? <em>{projectToDelete.name}</em> : "this project"}
            ? Its shared chat is removed and members&apos; personal chats are
            detached.
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setProjectIdToDelete(null)}
              data-testid="sidebar-delete-project-cancel"
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() =>
                projectIdToDelete && handleDeleteProject(projectIdToDelete)
              }
              disabled={deleteProjectMutation.isPending}
              loading={deleteProjectMutation.isPending}
              data-testid="sidebar-delete-project-confirm"
            >
              <Trash />
              Delete
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root
        open={chatIdToDelete != null}
        onOpenChange={(open) => {
          if (!open) setChatIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Delete chat?</Dialog.Title>
          <Dialog.Description>
            Are you sure you want to delete{" "}
            {chatToDelete ? <em>{chatToDelete.title}</em> : "this chat"}?
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setChatIdToDelete(null)}
              data-testid="sidebar-delete-chat-cancel"
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => chatIdToDelete && handleDeleteChat(chatIdToDelete)}
              disabled={deleteChatMutation.isPending}
              loading={deleteChatMutation.isPending}
              data-testid="sidebar-delete-chat-confirm"
            >
              <Trash />
              Delete
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
